import React, { useState } from 'react';
import { Box, Button, Snackbar, Alert, Tooltip, Typography } from '@mui/material';
import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import { triggerAssessmentQC } from '../../services/qcReview';

interface RunQCButtonProps {
  workflowId: number;
  eligible: boolean;
  ineligibleReason?: string;
}

// Mirrors ReportRequestButton's trigger-then-confirm pattern (handoff §3: fire-and-forget,
// no polling, no status widget — completion arrives later by email).
const RunQCButton: React.FC<RunQCButtonProps> = ({ workflowId, eligible, ineligibleReason }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Failed to start the QC run. Please try again.');

  const handleRunQC = async () => {
    setIsRunning(true);
    try {
      await triggerAssessmentQC(workflowId);
      setShowConfirmation(true);
    } catch (error: any) {
      console.error('Failed to trigger QC run', error);
      const detail = Array.isArray(error?.response?.data) ? error.response.data[0] : undefined;
      setErrorMessage(detail || 'Failed to start the QC run. Please try again.');
      setErrorOpen(true);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <>
      <Tooltip title={!eligible ? ineligibleReason : ''}>
        <span>
          <Button
            variant="outlined"
            size="small"
            disabled={!eligible || isRunning}
            onClick={handleRunQC}
          >
            {isRunning ? 'Starting...' : 'Run QC'}
          </Button>
        </span>
      </Tooltip>
      <Snackbar
        open={errorOpen}
        autoHideDuration={3000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorOpen(false)} severity="error" variant="filled">
          {errorMessage}
        </Alert>
      </Snackbar>
      <StandardDialog
        isOpen={showConfirmation}
        handleClose={() => setShowConfirmation(false)}
        title="QC Started"
      >
        <Typography>
          QC started. You{'’'}ll be emailed when it{'’'}s ready to review.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <StandardDialogActions>
            <Button variant="outlined" onClick={() => setShowConfirmation(false)}>
              Close
            </Button>
          </StandardDialogActions>
        </Box>
      </StandardDialog>
    </>
  );
};

export default RunQCButton;
